// DataAdvisory.jsx
import React, { useRef, useEffect, useState } from 'react';
import './DataAdvisory.css';
import data from "../../../assets/services/data.png";

import {
  IoAnalyticsOutline,
  IoStatsChartOutline,
  IoShieldCheckmarkOutline,
  IoTrendingUpOutline,
  IoCloudOutline,
  IoBarChartOutline,
  IoLockClosedOutline,
  IoBusinessOutline,
} from 'react-icons/io5';
import {
  FiDatabase,
  FiServer,
  FiLayers,
  FiBarChart2,
  FiCpu,
} from 'react-icons/fi';
import {
  MdOutlineAutoAwesome,
  MdOutlineDataUsage,
  MdOutlineDashboard,
} from 'react-icons/md';

/* ── Data ─────────────────────────────────────────────────── */
const deliverables = [
  { title: "Enterprise Data Strategy", icon: <FiDatabase /> },
  { title: "Data Governance Framework", icon: <IoShieldCheckmarkOutline /> },
  { title: "Data Architecture & Modernization", icon: <FiServer /> },
  { title: "Data Quality & Master Data Management", icon: <MdOutlineDataUsage /> },
  { title: "Business Intelligence & Analytics Strategy", icon: <MdOutlineDashboard /> },
  { title: "AI & Data Readiness Assessment", icon: <FiCpu /> },
  { title: "Data Security & Compliance Advisory", icon: <IoLockClosedOutline /> },
  { title: "KPI & Executive Reporting Frameworks", icon: <FiBarChart2 /> },
  { title: "Data Operating Model Design", icon: <FiLayers /> },
];

const outcomes = [
  {
    text: "Better Decision-Making",
    description: "Enable leadership teams with trusted, real-time business insights.",
    icon: <IoAnalyticsOutline />,
  },
  {
    text: "Improved Operational Efficiency",
    description: "Reduce duplication, inconsistencies, and manual reporting processes.",
    icon: <IoStatsChartOutline />,
  },
  {
    text: "Stronger Data Governance",
    description: "Improve data accuracy, compliance, privacy, and accountability.",
    icon: <IoShieldCheckmarkOutline />,
  },
  {
    text: "AI & Automation Readiness",
    description: "Create a strong foundation for AI, predictive analytics, and intelligent automation.",
    icon: <MdOutlineAutoAwesome />,
  },
  {
    text: "Scalable Digital Growth",
    description: "Build modern data ecosystems that scale with evolving business needs.",
    icon: <IoTrendingUpOutline />,
  },
];

const whyBairees = [
  { text: "Business-first data strategy", icon: <IoBusinessOutline /> },
  { text: "Modern cloud-native architectures", icon: <IoCloudOutline /> },
  { text: "Governance and compliance alignment", icon: <IoShieldCheckmarkOutline /> },
  { text: "Scalable analytics foundations", icon: <IoBarChartOutline /> },
  { text: "AI-ready enterprise data environments", icon: <FiCpu /> },
];

const stats = [
  { value: 150, suffix: "+", label: "Data strategies delivered" },
  { value: 60, suffix: "%", label: "Faster decision cycles" },
  { value: 3, suffix: "x", label: "Avg. ROI on data investments" },
  { value: 100, suffix: "%", label: "Business-outcome focused" },
];

/* ── Counter hook ──────────────────────────────────────────── */
function useCountUp(target, duration = 1800, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return value;
}

function StatCard({ value, suffix, label, delay, inView }) {
  const count = useCountUp(value, 1600, inView);
  return (
    <div className="da-stat" style={{ animationDelay: `${delay}ms` }}>
      <span className="da-stat__number">{count}{suffix}</span>
      <span className="da-stat__label">{label}</span>
    </div>
  );
}

/* ── Component ─────────────────────────────────────────────── */
const DataAdvisory = () => {
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="da">

      {/* ── HERO ──────────────────────────────────────────── */}
      <section className="da-hero">
        <div className="da-hero__bg-grid" aria-hidden />
        <div className="da-hero__container">

          <div className="da-hero__content">
            <p className="da-eyebrow">Data Strategy Advisory</p>
            <h1 className="da-hero__title">
              Turn Data Into<br />
              <span className="da-hero__title--gold">Business Intelligence</span>
            </h1>
            <p className="da-hero__tagline">Strategy. Governance. AI Readiness.</p>
            <p className="da-hero__desc">
              Modern organizations generate massive volumes of data — but without a clear strategy,
              data remains fragmented and disconnected from business outcomes. Bairees helps you
              build scalable, secure, and business-aligned data strategies that enable smarter
              decisions and long-term digital growth.
            </p>
            <div className="da-hero__actions">
              <a href="/contact">
                <button className="da-btn da-btn--primary">Talk to Our Data Experts <span className="arrow">→</span></button>
              </a>
              <a href="/contact">
                <button className="da-btn da-btn--ghost">View Framework</button>
              </a>
            </div>
          </div>

          <div className="da-hero__aside">
            <div className="da-hero__card">
              <div className="da-hero__card-icon"><FiDatabase /></div>
              <p className="da-hero__card-label">Foundation</p>
              <p className="da-hero__card-value">Enterprise Data Strategy</p>
            </div>
            <div className="da-hero__card">
              <div className="da-hero__card-icon"><MdOutlineAutoAwesome /></div>
              <p className="da-hero__card-label">Intelligence</p>
              <p className="da-hero__card-value">AI & Analytics Readiness</p>
            </div>
            <div className="da-hero__card">
              <div className="da-hero__card-icon"><IoShieldCheckmarkOutline /></div>
              <p className="da-hero__card-label">Control</p>
              <p className="da-hero__card-value">Governance & Compliance</p>
            </div>
          </div>

        </div>
      </section>

      {/* ── STATS ─────────────────────────────────────────── */}
      <section className="da-stats" ref={statsRef}>
        <div className="da-stats__inner">
          {stats.map((s, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className="da-stats__divider" />}
              <StatCard {...s} delay={i * 150} inView={statsVisible} />
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ── WHAT WE DELIVER ───────────────────────────────── */}
      <section className="da-section">
        <div className="da-section__container">
          <div className="da-section__header">
            <p className="da-eyebrow">Capabilities</p>
            <h2 className="da-section__title">What We Deliver</h2>
            <p className="da-section__subtitle">
              A complete data advisory suite — from strategy and architecture to governance and AI readiness.
            </p>
          </div>
          <div className="da-deliverables">
            {deliverables.map((item, i) => (
              <div key={i} className="da-deliverable" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="da-deliverable__icon">{item.icon}</div>
                <p className="da-deliverable__text">{item.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FRAMEWORK ─────────────────────────────────────── */}
      {/* <section className="da-framework">
        <div className="da-framework__container">
          <div className="da-framework__header">
            <p className="da-eyebrow da-eyebrow--light">Our Approach</p>
            <h2 className="da-framework__title">Data Strategy Framework</h2>
          </div>
          <div className="da-framework__visual">
            <img src={data} alt="Data Strategy Framework" className="da-framework__img" />
          </div>
        </div>
      </section> */}

      {/* ── OUTCOMES ──────────────────────────────────────── */}
      <section className="da-section da-section--alt">
        <div className="da-section__container">
          <div className="da-section__header">
            <p className="da-eyebrow">What You Gain</p>
            <h2 className="da-section__title">Key Business Outcomes</h2>
            <p className="da-section__subtitle">
              Measurable results that connect your data investments to business performance.
            </p>
          </div>
          <div className="da-outcomes">
            {outcomes.map((item, i) => (
              <div key={i} className="da-outcome" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="da-outcome__icon">{item.icon}</div>
                <h3 className="da-outcome__title">{item.text}</h3>
                <p className="da-outcome__desc">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY BAIREES ───────────────────────────────────── */}
      <section className="da-why">
        <div className="da-why__container">
          <div className="da-why__left">
            <p className="da-eyebrow da-eyebrow--light">Our Edge</p>
            <h2 className="da-why__title">Why Bairees</h2>
            <p className="da-why__body">
              Bairees combines business consulting expertise with deep data and technology advisory.
              We help organizations move beyond isolated reporting systems and create enterprise-wide
              data ecosystems that drive measurable business outcomes.
            </p>
            <div className="da-why__chips">
              <span>✓ Beyond isolated reporting</span>
              <span>✓ Enterprise-wide ecosystems</span>
              <span>✓ Measurable business outcomes</span>
            </div>
          </div>
          <div className="da-why__right">
            <p className="da-why__focus-label">We focus on</p>
            <ul className="da-list">
              {whyBairees.map((item, i) => (
                <li key={i} className="da-list__item">
                  <span className="da-list__icon">{item.icon}</span>
                  <span className="da-list__text">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────── */}
      <section className="next-section">
        <div className="next-container">
          <div className="next-left">
            <h2 className="next-title">Build a Data-Driven Enterprise</h2>
            <p className="next-subtitle">
              Transform disconnected data into actionable intelligence with a modern
              data strategy aligned to your business goals.
            </p>
            <div className="da-cta__chips">
              <span>✓ Trusted insights</span>
              <span>✓ AI-ready foundation</span>
              <span>✓ Scalable governance</span>
              <span>✓ Faster decisions</span>
            </div>
          </div>
          <div className="next-right">
            <a href="/contact">
              <button className="next-btn">Start Here <span className="arrow">→</span></button>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default DataAdvisory;